export interface SolarRentCollectionSubStepConfig {
  index: number;
  label: string;
  step: number;
  title: string;
  subSelected: string;
}

export interface SolarRentSignatureDocumentConfig {
  group?: {
    key: string;
    label: string;
    title: string;
    code: string;
    showToClient: boolean;
  } | null;
  index: number;
  key: string;
  groupKey: string;
  semanticKey: string;
  code: string;
  pdfTemplateKey: string;
  label: string;
  needUserSignature: boolean;
  fileName: string;
  description: string;
  step: number;
  title: string;
  subSelected: string;
  previewUrl: string;
  type: 'document' | 'final-signature';
}

export interface SolarRentDocumentStatusEntry {
  available: boolean;
  applicable: boolean;
  signed: boolean;
  transmitted: boolean;
  input_value?: string;
}

export interface SolarRentDocumentStatusBaselineRow {
  id: string;
  label: string;
  code?: string;
  labelKey?: string;
  inputLabelKey?: string;
}

export const SOLAR_RENT_DOCUMENT_STATUS_BASELINE_ROWS: SolarRentDocumentStatusBaselineRow[] = [
  {
    id: 'principal-operating-instructions',
    label: 'Principal operating instructions',
    labelKey: 'shared.solar_rent.document_status.principal_operating_instructions',
  },
  {
    id: 'safety-instructions',
    label: 'Safety instructions',
    labelKey: 'shared.solar_rent.document_status.safety_instructions',
  },
  {
    id: 'monitoring-application-information',
    label: 'Monitoring / application information',
    labelKey: 'shared.solar_rent.document_status.monitoring_application_information',
  },
  {
    id: 'installed-configuration-sheet',
    label: 'Installed Configuration Sheet',
    labelKey: 'shared.solar_rent.document_status.installed_configuration_sheet',
  },
  {
    id: 'diagram-technical-document-useful-to-client',
    label: 'Diagram / technical document useful to the Client',
    labelKey: 'shared.solar_rent.document_status.diagram_technical_document_useful_to_client',
  },
  {
    id: 'after-sales-maintenance-contact-details',
    label: 'After-sales / Maintenance contact details',
    labelKey: 'shared.solar_rent.document_status.after_sales_maintenance_contact_details',
  },
  {
    id: 'applicable-grid-or-ceb-documents',
    label: 'Applicable grid or CEB documents',
    labelKey: 'shared.solar_rent.document_status.applicable_grid_or_ceb_documents',
  },
  { id: 'applicable-simulation-economic-study', label: 'Applicable simulation / economic study' },
  { id: 'proof-of-receipt-of-the-plm', label: 'Proof of actual receipt of the PLM' },
  {
    id: 'other',
    label: 'Other useful information document',
    inputLabelKey: 'shared.solar_rent.document_status.other_value_aria_label',
  },
];

export function normalizeSolarRentDocumentStatusText(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
}

export function matchesSolarRentDocumentStatusBaselineRow(
  row: SolarRentDocumentStatusBaselineRow,
  template: { code: string; label: string },
): boolean {
  const templateCode = normalizeSolarRentDocumentStatusText(template.code);
  const templateName = normalizeSolarRentDocumentStatusText(template.label);

  if (row.code !== undefined && row.code !== '') {
    const code = normalizeSolarRentDocumentStatusText(row.code);
    if (code !== '' && (templateCode.includes(code) || templateName.includes(code))) {
      return true;
    }

    const baseName = normalizeSolarRentDocumentStatusText(
      row.label.startsWith(row.code) ? row.label.slice(row.code.length) : row.label,
    );
    if (baseName !== '' && (templateName === baseName || templateName.includes(baseName))) {
      return true;
    }

    return false;
  }

  const label = normalizeSolarRentDocumentStatusText(row.label);
  return label !== '' && (templateName === label || templateName.includes(label));
}

export interface SolarRentSignatureDocumentTemplatesPendingRequest {
  requestKey: string;
  requestBaseUrl: string;
  selectedProductType: string;
  selectedProductName: string;
  baseUrl: string;
  token: string | null;
}

export const SOLAR_RENT_BANK_FINANCING_HIDDEN_DOCUMENT_CODES = [
  '12SRH-FIN/MAN02',
  '14SRH-PLM02',
] as const;

export const SOLAR_RENT_SINGLE_SIGNATURE_FOR_ALL = true;

export const SOLAR_RENT_BINARY_CHOICE_VALUES = {
  yes: 'yes',
  no: 'no',
} as const;

export type SolarRentBinaryChoiceValue =
  (typeof SOLAR_RENT_BINARY_CHOICE_VALUES)[keyof typeof SOLAR_RENT_BINARY_CHOICE_VALUES];

export const SOLAR_RENT_TENANT_CIVILITY_VALUES = [
  'mr',
  'ms',
  'mr_and_ms',
] as const;

export const SOLAR_RENT_TENANT_CIVILITY_LABELS: Record<(typeof SOLAR_RENT_TENANT_CIVILITY_VALUES)[number], string> = {
  mr: 'shared.solar_rent.civility.mr',
  ms: 'shared.solar_rent.civility.ms',
  mr_and_ms: 'shared.solar_rent.civility.mr_and_ms',
};

export const SOLAR_RENT_TENANT_CIVILITY_OPTIONS = SOLAR_RENT_TENANT_CIVILITY_VALUES.map((value) => ({
  value,
  label: SOLAR_RENT_TENANT_CIVILITY_LABELS[value],
}));

export const SOLAR_RENT_PAYMENT_FREQUENCY_VALUES = [
  'monthly',
  'yearly',
] as const;

export type SolarRentPaymentFrequencyValue = (typeof SOLAR_RENT_PAYMENT_FREQUENCY_VALUES)[number];

export const SOLAR_RENT_PAYMENT_FREQUENCY_LABELS: Record<(typeof SOLAR_RENT_PAYMENT_FREQUENCY_VALUES)[number], string> = {
  monthly: 'shared.solar_rent.financial.frequency_monthly',
  yearly: 'shared.solar_rent.financial.frequency_yearly',
};

export const SOLAR_RENT_PAYMENT_FREQUENCY_OPTIONS = SOLAR_RENT_PAYMENT_FREQUENCY_VALUES.map((value) => ({
  value,
  label: SOLAR_RENT_PAYMENT_FREQUENCY_LABELS[value],
}));

export const SOLAR_RENT_PERIODIC_PAYMENT_MODE_VALUES = [
  'direct_debit',
  'bank_transfer',
  'standing_order',
  'other',
] as const;

export type SolarRentPeriodicPaymentModeValue = (typeof SOLAR_RENT_PERIODIC_PAYMENT_MODE_VALUES)[number];

export const SOLAR_RENT_PERIODIC_PAYMENT_MODE_LABELS: Record<SolarRentPeriodicPaymentModeValue, string> = {
  direct_debit: 'shared.solar_rent.financial.payment_mode_direct_debit',
  bank_transfer: 'shared.solar_rent.financial.payment_mode_bank_transfer',
  standing_order: 'shared.solar_rent.financial.payment_mode_standing_order',
  other: 'shared.solar_rent.common.other',
};

export const SOLAR_RENT_PERIODIC_PAYMENT_MODE_OPTIONS = SOLAR_RENT_PERIODIC_PAYMENT_MODE_VALUES.map((value) => ({
  value,
  label: SOLAR_RENT_PERIODIC_PAYMENT_MODE_LABELS[value],
}));

export const SOLAR_RENT_FIRST_RENT_PAYMENT_MODE_VALUES = [
  'cash_signature',
  'cash_before_installation',
  'schedule_accepted',
  'external_financing',
  'other',
] as const;

export type SolarRentFirstRentPaymentModeValue = (typeof SOLAR_RENT_FIRST_RENT_PAYMENT_MODE_VALUES)[number];

export const SOLAR_RENT_FIRST_RENT_PAYMENT_MODE_LABELS: Record<SolarRentFirstRentPaymentModeValue, string> = {
  cash_signature: 'shared.solar_rent.financial.cash_at_signature',
  cash_before_installation: 'shared.solar_rent.financial.cash_before_installation',
  schedule_accepted: 'shared.solar_rent.financial.schedule_accepted',
  external_financing: 'shared.solar_rent.financial.external_financing',
  other: 'shared.solar_rent.common.other',
};

export const SOLAR_RENT_FIRST_RENT_PAYMENT_MODE_OPTIONS = SOLAR_RENT_FIRST_RENT_PAYMENT_MODE_VALUES.map((value) => ({
  value,
  label: SOLAR_RENT_FIRST_RENT_PAYMENT_MODE_LABELS[value],
}));

export const SOLAR_RENT_OCCUPATION_STATUS_VALUES = [
  'owner_occupant',
  'authorized_occupant',
] as const;

export const SOLAR_RENT_OCCUPATION_STATUS_LABELS: Record<(typeof SOLAR_RENT_OCCUPATION_STATUS_VALUES)[number], string> = {
  owner_occupant: 'shared.solar_rent.location.occupation_owner_occupant',
  authorized_occupant: 'shared.solar_rent.location.occupation_authorized_occupant',
};

export const SOLAR_RENT_OCCUPATION_STATUS_OPTIONS = SOLAR_RENT_OCCUPATION_STATUS_VALUES.map((value) => ({
  value,
  label: SOLAR_RENT_OCCUPATION_STATUS_LABELS[value],
}));

export const SOLAR_RENT_CONFIGURATION_VERSION_VALUES = [
  'initial_configuration',
  'update_after_intervention',
  'update_after_replacement',
  'update_after_technical_adaptation',
] as const;

export const SOLAR_RENT_CONFIGURATION_VERSION_LABELS: Record<(typeof SOLAR_RENT_CONFIGURATION_VERSION_VALUES)[number], string> = {
  initial_configuration: 'shared.solar_rent.purpose.config_version_initial',
  update_after_intervention: 'shared.solar_rent.purpose.config_version_intervention',
  update_after_replacement: 'shared.solar_rent.purpose.config_version_replacement',
  update_after_technical_adaptation: 'shared.solar_rent.purpose.config_version_technical_adaptation',
};

export const SOLAR_RENT_CONFIGURATION_VERSION_OPTIONS = SOLAR_RENT_CONFIGURATION_VERSION_VALUES.map((value) => ({
  value,
  label: SOLAR_RENT_CONFIGURATION_VERSION_LABELS[value],
}));

export const SOLAR_RENT_TENANT_COMPLETION_MENTION = 'shared.solar_rent.signatures.mention_completion';

export function getSolarRentTenantCivilityLabel(value: string): string {
  const normalized = normalizeSolarRentTenantCivilityValue(value);
  return normalized
    ? SOLAR_RENT_TENANT_CIVILITY_LABELS[normalized as (typeof SOLAR_RENT_TENANT_CIVILITY_VALUES)[number]]
    : value.trim();
}

export function getSolarRentPaymentFrequencyLabel(value: string): string {
  const normalized = normalizeSolarRentPaymentFrequencyValue(value);
  return normalized
    ? SOLAR_RENT_PAYMENT_FREQUENCY_LABELS[normalized as (typeof SOLAR_RENT_PAYMENT_FREQUENCY_VALUES)[number]]
    : value.trim();
}

export function getSolarRentOccupationStatusLabel(value: string): string {
  const normalized = normalizeSolarRentOccupationStatusValue(value);
  return normalized
    ? SOLAR_RENT_OCCUPATION_STATUS_LABELS[normalized as (typeof SOLAR_RENT_OCCUPATION_STATUS_VALUES)[number]]
    : value.trim();
}

export function normalizeSolarRentTenantCivilityValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'mr' || normalized === 'monsieur') return 'mr';
  if (normalized === 'ms' || normalized === 'madame') return 'ms';
  if (normalized === 'mr_and_ms' || normalized === 'monsieur et madame') return 'mr_and_ms';
  return value.trim();
}

export function normalizeSolarRentPaymentFrequencyValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'monthly' || normalized === 'mensuelle') return 'monthly';
  if (normalized === 'yearly' || normalized === 'annuelle') return 'yearly';
  return value.trim();
}

export function normalizeSolarRentPeriodicPaymentModeValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'direct_debit' || normalized === 'prelevement automatique' || normalized === 'direct debit') {
    return 'direct_debit';
  }
  if (normalized === 'bank_transfer' || normalized === 'virement' || normalized === 'bank transfer') {
    return 'bank_transfer';
  }
  if (normalized === 'standing_order' || normalized === 'ordre permanent' || normalized === 'standing order') {
    return 'standing_order';
  }
  if (normalized === 'other' || normalized === 'autre') return 'other';
  return value.trim();
}

export function normalizeSolarRentOccupationStatusValue(value: string): string {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'owner_occupant' || normalized === 'proprietaire occupant') return 'owner_occupant';
  if (normalized === 'authorized_occupant' || normalized === 'occupant autorise') return 'authorized_occupant';
  return value.trim();
}

export function buildSolarRentSignatureDocumentId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function buildSolarRentSignatureDocumentPreviewUrl(fileName: string): string {
  return `/assets/pdfs/${encodeURIComponent(fileName)}`;
}

export function createSolarRentFinalSignatureDocumentConfig(index: number): SolarRentSignatureDocumentConfig {
  return {
    index,
    group: null,
    key: 'signature-final',
    groupKey: 'signature-final',
    semanticKey: '',
    code: '',
    pdfTemplateKey: '',
    label: 'shared.solar_rent.signatures.step_document_title',
    needUserSignature: true,
    fileName: '',
    description: 'shared.solar_rent.signatures.step_document_description',
    step: 200 + index,
    title: 'shared.solar_rent.signatures.step_document_title',
    subSelected: 'signature-final',
    previewUrl: '',
    type: 'final-signature',
  };
}

export interface SolarRentUploadDocumentConfig {
  key: string;
  label: string;
}

export interface SolarRentProductData {
  label: string;
  rangeKey: string;
  commercialReferenceKey: string;
  batteryCapacity: string;
  batteryUnitCount: string;
  panelCount: string;
  panelUnitPower: string;
  totalPvPower: string;
  inverterPower: string;
  maintenanceSemiAnnual: boolean;
  monitoringIncluded: boolean;
  hotlineIncluded: boolean;
  batteryReplacementGuaranteed: boolean;
  contractYears: string;
  cebNetMeteringIncluded: boolean;
  annualAuditIncluded: boolean;
}

export function getSolarRentPackageLabel(productData: SolarRentProductData | null | undefined): string {
  return productData?.label ?? '';
}

export function getSolarRentBatteryBrand(): string {
  return 'Sigenergy';
}

export function getSolarRentBatteryModel(): string {
  return 'LFP 8 kWh';
}

export function getSolarRentInverterLabel(productData: SolarRentProductData | null | undefined): string {
  return productData?.inverterPower ? productData.inverterPower : '';
}

export function getSolarRentMonitoringLabel(productData: SolarRentProductData | null | undefined): string {
  if (!productData) {
    return '';
  }

  return productData.monitoringIncluded ? 'Monitoring app inclus' : 'Monitoring non inclus';
}

export function getSolarRentMaintenanceLabel(productData: SolarRentProductData | null | undefined): string {
  if (!productData) {
    return '';
  }

  return productData.maintenanceSemiAnnual
    ? 'Maintenance preventive semestrielle'
    : 'Maintenance preventive annuelle';
}

export function getSolarRentPackageObservations(productData: SolarRentProductData | null | undefined): string {
  return [
    getSolarRentMonitoringLabel(productData),
    getSolarRentMaintenanceLabel(productData),
    productData?.hotlineIncluded ? 'Hotline 7j/7 incluse' : '',
    productData?.batteryReplacementGuaranteed ? 'Remplacement batterie garanti' : '',
    productData?.contractYears ? `Contrat LLDS ${productData.contractYears} ans` : '',
    productData?.cebNetMeteringIncluded ? 'Raccordement CEB net metering inclus' : '',
    productData?.annualAuditIncluded ? 'Audit energetique annuel' : '',
  ]
    .filter((value) => value !== '')
    .join(' | ');
}

function buildDateInputValue(date: Date): string {
  const pad = (value: number) => value.toString().padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

interface SolarRentPartyDefaults {
  legalName: string;
  legalForm: string;
  address: string;
  phone: string;
  email: string;
  platformApplication: string;
  businessRegistrationNumber: string;
  representedBy: string;
  title: string;
}

interface SolarRentSignatureDefaults {
  landlord: {
    name: string;
    title: string;
  };
  technician: {
    name: string;
    title: string;
  };
  tenant: {
    defaultMention: string;
  };
  acknowledgementTexts: {
    contract: string;
    mandate: string;
    technicalStudy: string;
    completionReport: string;
  };
}

function buildSolarRentFormDefaults(input: {
  contractTitle: string;
  defaultCountry: string;
  tenantCivilities: string[];
  productData: SolarRentProductData | null;
  parties: {
    landlord: SolarRentPartyDefaults;
    technician: SolarRentPartyDefaults;
  };
  signatures: SolarRentSignatureDefaults;
}) {
  const productData = input.productData;
  const packageObservations = getSolarRentPackageObservations(productData);

  return {
    contractType: '',
    latitude: '',
    longitude: '',
    address: {
      number: '',
      street: '',
      city: '',
      postcode: '',
      country: input.defaultCountry,
    },
    contractDetails: {
      contractReference: '',
      fileReference: '',
      tenantClient: '',
      signatureDate: '',
      installationDate: '',
      commissioningDate: '',
      dossierParticularities: '',
      parties: {
        landlord: {
          legalName: input.parties.landlord.legalName,
          legalForm: input.parties.landlord.legalForm,
          address: input.parties.landlord.address,
          phone: input.parties.landlord.phone,
          email: input.parties.landlord.email,
          platformApplication: input.parties.landlord.platformApplication,
          businessRegistrationNumber: input.parties.landlord.businessRegistrationNumber,
          representedBy: input.parties.landlord.representedBy,
          title: input.parties.landlord.title,
        },
        technician: {
          legalName: input.parties.technician.legalName,
          legalForm: input.parties.technician.legalForm,
          address: input.parties.technician.address,
          phone: input.parties.technician.phone,
          email: input.parties.technician.email,
          platformApplication: input.parties.technician.platformApplication,
          businessRegistrationNumber: input.parties.technician.businessRegistrationNumber,
          representedBy: input.parties.technician.representedBy,
          title: input.parties.technician.title,
        },
        tenant: {
          financedByBank: SOLAR_RENT_BINARY_CHOICE_VALUES.yes as SolarRentBinaryChoiceValue,
          civility: input.tenantCivilities[0] ?? '',
          companyName: '',
          name: '',
          address: '',
          identityNumber: '',
          phone: '',
          email: '',
        },
        documentLanguage: 'fr',
        site: {
          fullAddress: '',
          siteReference: '',
          technicalReference: '',
          previousAddress: {
            number: '',
            street: '',
            city: '',
            postcode: '',
            country: '',
          },
          previousCoordinates: {
            latitude: '',
            longitude: '',
          },
          propertyType: '',
          occupationStatus: '',
          siteOwnerName: '',
          roofCondition: '',
          structureType: '',
          generalInstallationMode: '',
          plannedInstallationArea: '',
          specificConstraints: '',
          mountingOtherDetails: '',
          roofAge: '',
          roofLoadCapacity: '',
          architecturalRestrictions: '',
          siteConstraints: '',
          easements: '',
          neighborConsents: '',
          singlePhaseEnvironment: true,
          threePhaseEnvironment: false,
          electricalEnvironmentOther: '',
        },
      },
      referenceConfig: {
        range: productData?.label ?? '',
        commercialReference: productData?.commercialReferenceKey ?? '',
        plannedPvPower: productData?.totalPvPower ?? '',
        plannedBatteryCapacity: productData?.batteryCapacity ?? '',
        plannedInverter: getSolarRentInverterLabel(productData),
        plannedMonitoringYes: productData?.monitoringIncluded === true,
        plannedMonitoringNo: productData ? !productData.monitoringIncluded : false,
        associatedInfrastructurePlanned: null as boolean | null,
        associatedInfrastructureAsPerTechnicalAnnex: false,
        configurationVersion: '',
        otherEssentialCharacteristics: '',
        previousContractReference: '',
        moduleCount: '',
        moduleUnitPower: '',
        moduleTechnology: '',
        electricalEnvironmentOther: '',
        converterDeviceCount: '',
        converterSpecificFunctions: '',
        batteryUsableCapacity: '',
        batteryChargeDischargePower: '',
        batteryUnitCount: '',
        batteryPlannedLocation: '',
        batterySpecificFunctions: '',
        incidentReportingPhone: '',
        incidentReportingEmail: '',
        incidentReportingPlatform: '',
        validationTechnicalYes: false,
        validationTechnicalNo: false,
        validationTechnicalObservation: '',
        firstRentFinancingYes: false,
        firstRentFinancingNo: false,
        firstRentFinancingInstitution: '',
        cebHouseholdsSchemeYes: false,
        cebHouseholdsSchemeNo: false,
        cebHouseholdsSchemeObservation: '',
        tenantRequiredDocumentsYes: false,
        tenantRequiredDocumentsNo: false,
        tenantRequiredDocumentsObservation: '',
        otherSuspensiveConditions: '',
        suspensiveConditions: '',
        suspensiveDetails: '',
        regulatoryApprovals: '',
        otherUsefulDetails: '',
      },
      cetBis: {
        cetBisReasonDigitalCetImpossible: false,
        cetBisReasonInsufficientPhotographs: false,
        cetBisReasonInsufficientVideos: false,
        cetBisReasonMissingMeasurements: false,
        cetBisReasonInsufficientElectricalData: false,
        cetBisReasonSpTpEnvironment: false,
        cetBisReasonElectricalPanel: false,
        cetBisReasonBatteryInverterLocation: false,
        cetBisReasonPvLayout: false,
        cetBisReasonTechnicalAccess: false,
        cetBisReasonContradictoryInformation: false,
        cetBisReasonSpecificSiteConstraints: false,
        cetBisReasonOther: false,
        cetBisReasonOtherValue: '',
        cetBisSpecificElements: '',
        cetBisOutcomeSufficientInformation: false,
        cetBisOutcomeFurtherInformationRequired: false,
        cetBisOutcomeConfigurationCompatible: false,
        cetBisOutcomeConfigurationToAdapt: false,
        cetBisOutcomeAdditionalSpecializedStudy: false,
        cetBisOutcomeTechnicallyIncompatible: false,
        cetBisOutcomeOther: false,
        cetBisOutcomeOtherValue: '',
      },
      energyFrameworkGrid: {
        noSpecificGridFormalityApplicable: false,
        administrativeFileBeingPrepared: false,
        fileSubmitted: false,
        approvalValidationObtained: false,
        connectionAgreementObtained: false,
        certificateOfInstallationIssued: false,
        connectionMeterFinalValidationPending: false,
        energyFrameworkOther: false,
        energyFrameworkOtherValue: '',
        ceb: false,
        otherGridOperator: false,
        authorityBody: false,
        authorityBodyValue: '',
      },
      documentStatus: {
        otherValue: '',
        otherDetails: '',
      },
      financialTerms: {
        financingRequested: '',
        financingAmountRequested: '',
        financingPaymentReference: '',
        initialMonthlyRentTtc: '',
        initialMonthlyRentHt: '',
        firstRentAmount: '',
        // Simplified scoring for Premier Loyer Majore (17SRH-PLM05)
        firstRentScoringClientBankedYes: false,
        firstRentScoringClientBankedNo: false,
        firstRentScoringRegularIncomeYes: false,
        firstRentScoringRegularIncomeNo: false,
        firstRentScoringBankHistoryAcceptableYes: false,
        firstRentScoringBankHistoryAcceptableNo: false,
        firstRentScoringSiteEligibleYes: false,
        firstRentScoringSiteEligibleNo: false,
        firstRentScoringContractSignedYes: false,
        firstRentScoringContractSignedNo: false,
        firstRentScoringRentDocumentedYes: false,
        firstRentScoringRentDocumentedNo: false,
        firstRentScoringDirectPaymentOperatorYes: false,
        firstRentScoringDirectPaymentOperatorNo: false,
        firstRentPaymentModeCashSignature: false,
        firstRentPaymentModeCashBeforeInstallation: false,
        firstRentPaymentModeScheduleAccepted: false,
        firstRentScheduleInstallment1Amount: '',
        firstRentScheduleInstallment1Date: '',
        firstRentScheduleInstallment2Amount: '',
        firstRentScheduleInstallment2Date: '',
        firstRentScheduleInstallment3Amount: '',
        firstRentScheduleInstallment3Date: '',
        firstRentPaymentModeExternalFinancing: false,
        firstRentPaymentModeOther: false,
        firstRentPaymentModeOtherDetails: '',
        firstRentExternalFinancingInstitution: '',
        firstRentExternalFinancingReference: '',
        firstRentRemainingPayableByLessee: '',
        firstRentExternalAmount: '',
        firstRentExternalIndicativeMonthlyPayment: '',
        firstRentExternalDuration: '',
        firstRentExternalDisbursementDate: '',
        firstPaymentDueDate: '',
        paymentFrequency: SOLAR_RENT_PAYMENT_FREQUENCY_VALUES[0] as SolarRentPaymentFrequencyValue,
        periodicPaymentMode: '',
        periodicPaymentModeDirectDebit: false,
        periodicPaymentModeTransfer: false,
        periodicPaymentModeStandingOrder: false,
        periodicPaymentModeOther: false,
        periodicPaymentModeOtherDetails: '',
        deferredFinancialStartDate: '',
        deferredFinancialStartReason: '',
        rentRevisionFormula: '',
        latePaymentPenalty: '',
        securityDepositAmount: '',
        insuranceResponsibleBy: '',
        insuranceAmount: '',
        earlyTerminationPenalty: '',
      },
      cebInformation: {
        cebReferenceDate: '',
        cebReferenceTariff: '',
        cebTariffCategory: '',
        cebReferenceKwhValue: '',
        cebApplicableScheme: '',
        cebBusinessPartnerNumber: '',
        cebContractAccountNumbers: '',
      },
      mandatesPayment: {
        cebMeterReference: '',
        cebCustomerNumber: '',
        cebReferenceDate: '',
        cebReferenceTariff: '',
        cebTariffCategory: '',
        cebReferenceKwhValue: '',
        cebApplicableScheme: '',
        cebMandateScope: '',
        cebMandateStartDate: '',
        cebMandateEndDate: '',
        cebMandateDuration: '',
        bankName: '',
        iban: '',
        accountHolder: '',
        directDebitAuthorizationYes: false,
        directDebitAuthorizationNo: false,
        paymentModePrimary: '',
        paymentModeSecondary: '',
        mandateSignatureDate: '',
        paymentBeneficiary: 'Solar Rent LTD',
        paymentBank: 'Mauritius Commercial Bank',
        paymentAccountNumber: '',
        paymentIban: 'MU77MCBL0944000455000937000MUR',
        paymentMandatoryReference: '',
      },
      warrantiesSla: {
        maintenanceSchedule: '',
        maintenanceResponseTime: '',
        restorationTime: '',
        maintenanceExclusions: '',
        availabilityGuarantee: '',
        downtimeCompensation: '',
        batteryWarrantyCycles: '',
        batteryWarrantyDuration: '',
        performanceWarranty: '',
        tenantObligations: '',
        providerObligations: '',
      },
      installedConfig: {
        summary: {
          plannedPv: productData?.totalPvPower ?? '',
          installedPv: productData?.totalPvPower ?? '',
          pvCompliant: true,
          pvAdapted: false,
          pvOrdinaryAdaptation: false,
          pvSubstantialAdaptation: false,
          plannedBattery: productData?.batteryCapacity ?? '',
          installedBattery: productData?.batteryCapacity ?? '',
          batteryCompliant: true,
          batteryAdapted: false,
          batteryOrdinaryAdaptation: false,
          batterySubstantialAdaptation: false,
          plannedModuleCount: productData?.panelCount ?? '',
          installedModuleCount: productData?.panelCount ?? '',
          moduleCountCompliant: true,
          moduleCountAdapted: false,
          moduleCountOrdinaryAdaptation: false,
          moduleCountSubstantialAdaptation: false,
          plannedInverter: getSolarRentInverterLabel(productData),
          installedInverter: getSolarRentInverterLabel(productData),
          inverterCompliant: true,
          inverterAdapted: false,
          inverterOrdinaryAdaptation: false,
          inverterSubstantialAdaptation: false,
          plannedMonitoring: getSolarRentMonitoringLabel(productData),
          installedMonitoring: getSolarRentMonitoringLabel(productData),
          monitoringCompliant: true,
          monitoringAdapted: false,
          monitoringOrdinaryAdaptation: false,
          monitoringSubstantialAdaptation: false,
          plannedEnvironment: 'SP',
          installedEnvironment: 'SP',
          environmentCompliant: true,
          environmentAdapted: false,
          environmentOrdinaryAdaptation: false,
          environmentSubstantialAdaptation: false,
        },
        generalComment: packageObservations,
      },
      pvModules: {
        brand: 'Generic solar module',
        model: productData?.panelUnitPower ? `${productData.panelUnitPower} W` : '',
        unitPower: productData?.panelUnitPower ?? '',
        moduleCount: productData?.panelCount ?? '',
        totalPower: productData?.totalPvPower ?? '',
        moduleTechnology: '',
        serialNumbers: '',
        moduleTableRows: {},
        observations: packageObservations,
      },
      inverter: {
        brand: getSolarRentBatteryBrand(),
        model: getSolarRentInverterLabel(productData),
        nominalPower: productData?.inverterPower ?? '',
        converterDeviceCount: '',
        converterSpecificFunctions: '',
        hybridType: true,
        otherType: false,
        otherTypeDetails: '',
        serialNumber: '',
        location: '',
        observations: packageObservations,
      },
      battery: {
        brand: getSolarRentBatteryBrand(),
        model: getSolarRentBatteryModel(),
        nominalCapacity: productData?.batteryCapacity ?? '',
        usableCapacity: productData?.batteryCapacity ?? '',
        chargeDischargePower: '',
        unitCount: productData?.batteryUnitCount ?? '',
        serialNumbers: '',
        batteryTableRows: {},
        location: '',
        specificFunctions: '',
        observations: packageObservations,
      },
      protections: {
        acProtections: false,
        acProtectionsObservation: '',
        dcProtections: false,
        dcProtectionsObservation: '',
        disconnectionBox: false,
        disconnectionBoxObservation: '',
        surgeProtection: false,
        surgeProtectionObservation: '',
        mainWiring: false,
        mainWiringObservation: '',
        connectors: false,
        connectorsObservation: '',
        communicationInterfaces: false,
        communicationInterfacesObservation: '',
        gridMeteringInterface: false,
        gridMeteringInterfaceObservation: '',
        mountingElements: false,
        mountingElementsObservation: '',
        signage: false,
        signageObservation: '',
        other: false,
        otherDetails: '',
        observations: '',
      },
      monitoring: {
        installedYes: false,
        installedNo: false,
        description: '',
        application: getSolarRentMonitoringLabel(productData),
        connectivityWifi: false,
        connectivity4g: false,
        connectivityEthernet: false,
        connectivityOther: false,
        connectivityOtherDetails: '',
        accountCreatedYes: false,
        accountCreatedNo: false,
        accessProvidedYes: false,
        accessProvidedNo: false,
        reportingTestYes: false,
        reportingTestNo: false,
        reportingTestNotApplicable: false,
        testingProtocol: '',
        productionBaseline: '',
        supervisionYes: false,
        supervisionNo: false,
        controlYes: false,
        controlNo: false,
        observations: packageObservations,
      },
      siteLayout: {
        installationArea: '',
        modules: {
          mainRoof: false,
          secondaryRoof: false,
          dedicatedStructure: false,
          other: false,
          otherDetails: '',
          orientation: '',
          tilt: '',
          shadingYes: false,
          shadingNo: false,
          observations: '',
        },
        equipment: {
          batteryLocation: '',
          inverterLocation: '',
          enclosureLocation: '',
          maintenanceSatisfactory: false,
          maintenanceNeedsMonitoring: false,
          maintenanceReserved: false,
          maintenanceAccessConstraints: '',
          observations: '',
        },
      },
      associatedTechnicalInfrastructure: {
        notApplicable: false,
        present: false,
        tableRows: {},
        owningEntity: '',
        operatingTechnicalEntity: '',
        assetPortfolioReference: '',
      },
      deviations: {
        declaration: {
          none: false,
          minor: false,
          substantial: false,
        },
        minor: '',
        substantial: '',
        minorTableRows: {},
        substantialDeviation: {
          element: '',
          description: '',
          writtenAgreement: false,
          srhcpAmendment: false,
          contractualAmendment: false,
          other: false,
          otherDetails: '',
          acceptanceDocumentReference: '',
        },
      },
      expressDerogations: {
        none: false,
        relevantSrhsCgArticle: '',
        agreedDerogation: '',
        scopeOfDerogation: '',
      },
      reservations: {
        declaration: {
          withoutReservation: false,
          withMinorReservations: false,
          withSubstantialReservations: false,
        },
        punchListItems: '',
        remediationDeadline: '',
        provisionalAcceptanceDate: '',
        definitiveAcceptanceDate: '',
        minorReservations: '',
        minorTreatmentModalities: '',
        substantialReservations: '',
        actions: {
          commissioningMaintained: false,
          partialCommissioning: false,
          commissioningPostponed: false,
          amendmentRequired: false,
        },
      },
      commissioningChecks: {
        pvModulesPresent: { conforme: false, reserve: false, notApplicable: false },
        batteryPresent: { conforme: false, reserve: false, notApplicable: false },
        inverterPresent: { conforme: false, reserve: false, notApplicable: false },
        protectionsPresent: { conforme: false, reserve: false, notApplicable: false },
        mainConnectionsPresent: { conforme: false, reserve: false, notApplicable: false },
        systemEnergized: { conforme: false, reserve: false, notApplicable: false },
        initialSystemOperation: { conforme: false, reserve: false, notApplicable: false },
        initialStorageOperation: { conforme: false, reserve: false, notApplicable: false },
        conversionFunctions: { conforme: false, reserve: false, notApplicable: false },
        monitoringActive: { conforme: false, reserve: false, notApplicable: false },
        supervisionProvided: { conforme: false, reserve: false, notApplicable: false },
        clientApplicationPresented: { conforme: false, reserve: false, notApplicable: false },
        safetyInformationDelivered: { conforme: false, reserve: false, notApplicable: false },
        incidentReportingInformationDelivered: { conforme: false, reserve: false, notApplicable: false },
        maintenanceInformationDelivered: { conforme: false, reserve: false, notApplicable: false },
        technicalAccessIdentified: { conforme: false, reserve: false, notApplicable: false },
        configurationDocumentationCompleted: { conforme: false, reserve: false, notApplicable: false },
        serialNumbersIdentified: { conforme: false, reserve: false, notApplicable: false },
        assetOwnershipIdentified: { conforme: false, reserve: false, notApplicable: false },
      },
      documents: {
        regulatoryApprovals: '',
        requiredDocumentsChecklist: '',
        otherDetails: packageObservations,
        attachedTechnicalDocuments: {
          installationPhotographs: false,
          layoutPlan: false,
          singleLineDiagram: false,
          asBuiltDiagrams: false,
          serialNumberAssetRegister: false,
          ownershipAssetIdentificationRecord: false,
          technicalDatasheets: false,
          complianceDocuments: false,
          connectionDocument: false,
          srhPvms: false,
          supervisionScreenshot: false,
          other: false,
          otherDetails: '',
        },
      },
      additionalObservations: {
        landlordTechnician: '',
        tenant: '',
      },
      transmission: {
        withSrhPvms: false,
        byEmail: false,
        byWhatsApp: false,
        throughClientPortal: false,
        byElectronicLink: false,
        other: false,
        otherDetails: '',
        transmissionDate: '',
      },
      signatures: {
        date: buildDateInputValue(new Date()),
        landlord: {
          name: input.signatures.landlord.name,
          title: input.signatures.landlord.title,
          signature: '',
        },
        technician: {
          name: input.signatures.technician.name,
          title: input.signatures.technician.title,
          signature: '',
        },
        tenant: {
          name: '',
          signature: '',
          mention: input.signatures.acknowledgementTexts.completionReport,
        },
      },
    },
  };
}

export type SolarRentFormDefaults = ReturnType<typeof buildSolarRentFormDefaults>;

interface SolarRentDefaults {
  contractTitle: string;
  contractIntro: string;
  defaultCountry: string;
  signingPlace: string;
  checkOnlyIfLastPageReaded: boolean;
  tenantCivilities: string[];
  parties: {
    landlord: SolarRentPartyDefaults;
    technician: SolarRentPartyDefaults;
  };
  signatures: SolarRentSignatureDefaults;
  form: SolarRentFormDefaults;
}

export const SOLAR_RENT_DEFAULTS: SolarRentDefaults = {
  contractTitle: 'Fiche de Configuration Installee',
  contractIntro: 'au Contrat de Location Longue Duree de Solution Energetique Solaire avec Batterie',
  defaultCountry: 'Mauritius',
  signingPlace: 'Port-Louis, Mauritius',
  checkOnlyIfLastPageReaded: false,
  tenantCivilities: [...SOLAR_RENT_TENANT_CIVILITY_VALUES],
  parties: {
    landlord: {
      legalName: '',
      legalForm: '',
      address: '',
      phone: '',
      email: '',
      platformApplication: '',
      businessRegistrationNumber: '',
      representedBy: '',
      title: '',
    },
    technician: {
      legalName: '',
      legalForm: '',
      address: '',
      phone: '',
      email: '',
      platformApplication: '',
      businessRegistrationNumber: '',
      representedBy: '',
      title: '',
    },
  },
  signatures: {
    landlord: {
      name: 'Richard Biron',
      title: 'Directeur Général / General Manager',
    },
    technician: {
      name: 'Richard Biron',
      title: 'Directeur Général / General Manager',
    },
    tenant: {
      defaultMention: SOLAR_RENT_TENANT_COMPLETION_MENTION,
    },
    acknowledgementTexts: {
      contract: 'Lu, compris et approuve',
      mandate: 'lu et compris - Bon pour mandat',
      technicalStudy: 'lu et compris - Bon pour etude technique',
      completionReport: SOLAR_RENT_TENANT_COMPLETION_MENTION,
    },
  },
  form: buildSolarRentFormDefaults({
    contractTitle: 'Fiche de Configuration Installee',
    defaultCountry: 'Mauritius',
    tenantCivilities: [...SOLAR_RENT_TENANT_CIVILITY_VALUES],
    productData: null,
    parties: {
      landlord: {
        legalName: '',
        legalForm: '',
        address: '',
        phone: '',
        email: '',
        platformApplication: '',
        businessRegistrationNumber: '',
        representedBy: '',
        title: '',
      },
      technician: {
        legalName: '',
        legalForm: '',
        address: '',
        phone: '',
        email: '',
        platformApplication: '',
        businessRegistrationNumber: '',
        representedBy: '',
        title: '',
      },
    },
    signatures: {
      landlord: {
        name: 'Richard Biron',
        title: 'Directeur Général / General Manager',
      },
      technician: {
        name: 'Richard Biron',
        title: 'Directeur Général / General Manager',
      },
      tenant: {
        defaultMention: SOLAR_RENT_TENANT_COMPLETION_MENTION,
      },
      acknowledgementTexts: {
        contract: 'Lu, compris et approuve',
        mandate: 'lu et compris - Bon pour mandat',
        technicalStudy: 'lu et compris - Bon pour etude technique',
        completionReport: SOLAR_RENT_TENANT_COMPLETION_MENTION,
      },
    },
  }),
};

export function createSolarRentDefaultForm(): SolarRentFormDefaults {
  return JSON.parse(JSON.stringify(SOLAR_RENT_DEFAULTS.form)) as SolarRentFormDefaults;
}

export function buildSolarRentStepUrl(currentUrl: string, contractId: string, ...slugs: string[]): string {
  const segments = currentUrl.split('?')[0].split('/').filter((segment) => segment !== '');
  const contractIndex = contractId ? segments.lastIndexOf(contractId) : -1;
  const base = contractIndex >= 0 ? segments.slice(0, contractIndex + 1) : segments;
  return `/${[...base, ...slugs].join('/')}`;
}

export const SOLAR_RENT_COLLECTION_SUB_STEPS: SolarRentCollectionSubStepConfig[] = [
  {
    index: 1,
    label: 'shared.solar_rent.step.parties',
    step: 103,
    title: 'shared.solar_rent.step.parties',
    subSelected: 'parties',
  },
  {
    index: 2,
    label: 'shared.solar_rent.step.location',
    step: 102,
    title: 'shared.solar_rent.step.location',
    subSelected: 'site-location',
  },
  {
    index: 3,
    label: 'shared.solar_rent.step.purpose',
    step: 101,
    title: 'shared.solar_rent.step.purpose',
    subSelected: 'purpose',
  },
  {
    index: 4,
    label: 'shared.solar_rent.step.suspensive_conditions',
    step: 118,
    title: 'shared.solar_rent.step.suspensive_conditions',
    subSelected: 'suspensive-conditions',
  },
  {
    index: 5,
    label: 'shared.solar_rent.step.financial_terms',
    step: 104,
    title: 'shared.solar_rent.step.financial_terms',
    subSelected: 'financial-terms',
  },
  {
    index: 6,
    label: 'shared.solar_rent.step.ceb_scheme',
    step: 119,
    title: 'shared.solar_rent.step.ceb_scheme',
    subSelected: 'ceb-scheme',
  },
  {
    index: 7,
    label: 'shared.solar_rent.step.mandates_payment',
    step: 116,
    title: 'shared.solar_rent.step.mandates_payment',
    subSelected: 'mandates-payment',
  },
  {
    index: 8,
    label: 'shared.solar_rent.energy_framework_grid.title',
    step: 122,
    title: 'shared.solar_rent.energy_framework_grid.title',
    subSelected: 'energy-framework-grid',
  },
  {
    index: 9,
    label: 'shared.solar_rent.document_status.title',
    step: 123,
    title: 'shared.solar_rent.document_status.title',
    subSelected: 'document-status',
  },
  {
    index: 10,
    label: 'shared.solar_rent.step.installed_config',
    step: 105,
    title: 'shared.solar_rent.step.installed_config',
    subSelected: 'installed-config',
  },
  {
    index: 11,
    label: 'shared.solar_rent.step.pv_modules',
    step: 106,
    title: 'shared.solar_rent.step.pv_modules',
    subSelected: 'pv-modules',
  },
  {
    index: 12,
    label: 'shared.solar_rent.step.inverters',
    step: 107,
    title: 'shared.solar_rent.step.inverters',
    subSelected: 'inverters',
  },
  {
    index: 13,
    label: 'shared.solar_rent.step.battery',
    step: 108,
    title: 'shared.solar_rent.step.battery',
    subSelected: 'battery',
  },
  {
    index: 14,
    label: 'shared.solar_rent.step.protections',
    step: 109,
    title: 'shared.solar_rent.step.protections',
    subSelected: 'protections',
  },
  {
    index: 15,
    label: 'shared.solar_rent.step.monitoring',
    step: 110,
    title: 'shared.solar_rent.step.monitoring',
    subSelected: 'monitoring',
  },
  {
    index: 16,
    label: 'shared.solar_rent.step.site_layout',
    step: 111,
    title: 'shared.solar_rent.step.site_layout',
    subSelected: 'site-layout',
  },
  {
    index: 17,
    label: 'shared.solar_rent.step.associated_technical_infrastructure',
    step: 124,
    title: 'shared.solar_rent.step.associated_technical_infrastructure',
    subSelected: 'associated-technical-infrastructure',
  },
  {
    index: 18,
    label: 'shared.solar_rent.cet_bis.title',
    step: 121,
    title: 'shared.solar_rent.cet_bis.title',
    subSelected: 'cet-bis',
  },
  {
    index: 19,
    label: 'shared.solar_rent.step.warranties_sla',
    step: 117,
    title: 'shared.solar_rent.step.warranties_sla',
    subSelected: 'warranties-sla',
  },
  {
    index: 20,
    label: 'shared.solar_rent.step.deviations',
    step: 112,
    title: 'shared.solar_rent.step.deviations',
    subSelected: 'deviations',
  },
  {
    index: 21,
    label: 'shared.solar_rent.step.reservations',
    step: 113,
    title: 'shared.solar_rent.step.reservations',
    subSelected: 'reservations',
  },
  {
    index: 22,
    label: 'shared.solar_rent.step.commissioning_checks',
    step: 120,
    title: 'shared.solar_rent.step.commissioning_checks',
    subSelected: 'commissioning-checks',
  },
  {
    index: 23,
    label: 'shared.solar_rent.step.transmission',
    step: 125,
    title: 'shared.solar_rent.step.transmission',
    subSelected: 'transmission',
  },
  {
    index: 24,
    label: 'shared.solar_rent.step.documents',
    step: 114,
    title: 'shared.solar_rent.step.documents',
    subSelected: 'documents',
  },
  {
    index: 25,
    label: 'shared.solar_rent.step.observations',
    step: 115,
    title: 'shared.solar_rent.step.observations',
    subSelected: 'observations',
  },
];

export const SOLAR_RENT_UPLOAD_DOCUMENT_CONFIG: SolarRentUploadDocumentConfig[] = [
  {
    key: 'installation_photos',
    label: 'shared.solar_rent.upload.photos_installation',
  },
  {
    key: 'layout_plan',
    label: 'shared.solar_rent.upload.implantation_plan',
  },
  {
    key: 'single_line_diagram',
    label: 'shared.solar_rent.upload.single_line_diagram',
  },
  {
    key: 'equipment_datasheets',
    label: 'shared.solar_rent.upload.equipment_datasheets',
  },
  {
    key: 'serial_number_records',
    label: 'shared.solar_rent.upload.serial_number_records',
  },
  {
    key: 'monitoring_capture',
    label: 'shared.solar_rent.upload.monitoring_capture',
  },
  {
    key: 'commissioning_report',
    label: 'shared.solar_rent.upload.commissioning_report',
  },
  {
    key: 'compliance_certificate',
    label: 'shared.solar_rent.upload.certificate_conformity',
  },
  {
    key: 'ceb_document',
    label: 'shared.solar_rent.upload.ceb_document',
  },
  {
    key: 'other_document',
    label: 'shared.solar_rent.upload.other_documents',
  },
];

export const SOLAR_RENT_DOCUMENT_STATUS_META_KEYS: string[] = [
  'otherDetails',
  'otherValue',
];
